import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Button from 'ui/Button';
import CustomChip from 'ui/CustomChip';

import formattedDate from 'utils/formattedDate';

import ShipmentForm from './ShipmentForm';

const stateLabels = {
  shipped: 'Shipped',
  delivered: 'Delivered',
};

const stateStyles = {
  shipped: 'store',
  delivered: 'active',
};

// Mirrors the form's label-over-value reading, so the card is the view mode
// of what Edit Details opens.
const detailLabelStyle = {
  fontSize: '0.75em',
  opacity: 0.6,
  marginBottom: 2,
};

// One parcel: its items, tracking, notes, and who did what when. Every
// action names the fact it records, and the rightmost slot holds exactly
// one undo — the one for the parcel's most recent fact. Delivered undoes
// back to shipped; shipped undoes the shipment record itself (items return
// to Unshipped). Unshipping a delivered parcel therefore takes two steps.
// Undo Shipping confirms in place — a native confirm() would block the
// page (SELL21).
const ShipmentCard = ({ shipment, number, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const handleEditSubmit = async (values) => {
    await onUpdate(shipment, values);
    setEditing(false);
  };

  return (
    <div
      style={{
        border: '1px solid var(--borderColor, #eee)',
        borderRadius: 8,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{`Shipment #${number}`}</span>
          <CustomChip
            label={stateLabels[shipment.state] || shipment.state}
            styleKey={stateStyles[shipment.state]}
          />
        </span>
        <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {shipment.state === 'shipped' && (
            <Button highlighted smallPadded onClick={() => onUpdate(shipment, { state: 'delivered' })}>
              Register Delivery
            </Button>
          )}
          <Button secondary smallPadded onClick={() => setEditing(!editing)}>
            Edit Details
          </Button>
          {shipment.state === 'delivered' ? (
            <Button secondary smallPadded onClick={() => onUpdate(shipment, { state: 'shipped' })}>
              Undo Delivery
            </Button>
          ) : (
            // React's onBlur bubbles, so the wrapper disarms the two-step
            // confirm when focus leaves the button.
            <span onBlur={() => setConfirmingDelete(false)}>
              <Button
                secondary
                smallPadded
                onClick={() => (confirmingDelete ? onDelete(shipment) : setConfirmingDelete(true))}
              >
                {confirmingDelete ? 'Confirm Undo' : 'Undo Shipping'}
              </Button>
            </span>
          )}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {shipment.items.map((item) => (
          <div key={item.id} style={{ padding: '6px 0', borderTop: '1px solid var(--borderColor, #eee)' }}>
            {item.title}
            {item.variant_label && <strong> — {item.variant_label}</strong>}
          </div>
        ))}
      </div>

      {(shipment.tracking_number || shipment.tracking_url || shipment.notes) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
          {(shipment.tracking_number || shipment.tracking_url) && (
            <div>
              <div style={detailLabelStyle}>Tracking</div>
              {shipment.tracking_url ? (
                <a href={shipment.tracking_url} target="_blank" rel="noreferrer">
                  {shipment.tracking_number || shipment.tracking_url}
                </a>
              ) : (
                shipment.tracking_number
              )}
            </div>
          )}

          {shipment.notes && (
            <div>
              <div style={detailLabelStyle}>Notes</div>
              <div>{shipment.notes}</div>
            </div>
          )}
        </div>
      )}

      <div
        style={{
          borderTop: '1px solid var(--borderColor, #eee)',
          marginTop: 4,
          paddingTop: 8,
          fontSize: '0.85em',
          opacity: 0.7,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {shipment.shipped_at && (
          <div>
            {`Shipped ${formattedDate(shipment.shipped_at, 'datetime')}`}
            {shipment.shipped_by_name && ` by ${shipment.shipped_by_name}`}
          </div>
        )}
        {shipment.delivered_at && (
          <div>
            {`Delivered ${formattedDate(shipment.delivered_at, 'datetime')}`}
            {shipment.delivered_by_name && ` by ${shipment.delivered_by_name}`}
          </div>
        )}
      </div>

      {editing && (
        <ShipmentForm
          shipment={shipment}
          submitLabel="Save"
          onSubmit={handleEditSubmit}
          onCancel={() => setEditing(false)}
        />
      )}
    </div>
  );
};

ShipmentCard.propTypes = {
  shipment: PropTypes.object,
  number: PropTypes.number,
  onUpdate: PropTypes.func,
  onDelete: PropTypes.func,
};

export default ShipmentCard;
